import { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL, createAuthHeader } from '../config/api.config';

/**
 * Custom hook for managing tag filters
 * 
 * This hook provides a centralized system for tag filter values,
 * preventing unnecessary table refreshes when tags are added/removed while
 * maintaining up-to-date filter dropdown values.
 * 
 * @param {Object} params - Hook parameters
 * @param {string} params.clientId - The client ID for API requests
 * @param {Object} params.filteredInfo - Current table filters for faceted search
 * @param {Object} params.filterData - Initial filter data from main hook
 * @returns {Object} - { tagFilters, refreshFilters, loading, error }
 */
const useTagFilters = ({ clientId, filteredInfo = {}, filterData = null }) => {
  const [tagFilters, setTagFilters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [allTags, setAllTags] = useState([]);

  /**
   * Fetches fresh tag filter values from the API
   * Maintains faceted search by including current filter state
   */
  const fetchTagFilters = useCallback(async () => {
    if (!clientId) {
      setTagFilters([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        throw new Error('No access token available');
      }

      // Build query parameters
      const params = new URLSearchParams();
      params.append('clientId', clientId);

      // Add current filters to maintain faceted search
      Object.entries(filteredInfo).forEach(([key, values]) => {
        if (values && Array.isArray(values) && values.length > 0) {
          values.forEach(value => {
            params.append(`filters[${key}]`, value);
          });
        } else if (values && key === 'dynamicDateRange' && Array.isArray(values) && values.length === 2) {
          params.append(`filters[dateFrom]`, values[0]);
          params.append(`filters[dateTo]`, values[1]);
        }
      });

      const response = await axios.get(
        `${API_BASE_URL}/experiments/overview/filters?${params.toString()}`,
        {
        headers: createAuthHeader(token)
      });

      setTagFilters(response.data?.tags || []);
      setAllTags(response.data?.allTags || []);
    } catch (err) {
      console.error('Failed to fetch tag filters:', err);
      setError(err);
      setAllTags([]);
      // Maintain current filters on error to prevent UI disruption
    } finally {
      setLoading(false);
    }
  }, [clientId, filteredInfo]);

  /**
   * Refreshes the filters by fetching fresh data
   * Called after tag operations to ensure dropdown stays current
   */
  const refreshFilters = useCallback(() => {
    fetchTagFilters();
  }, [fetchTagFilters]);

  // Sync filters with filterData whenever it changes
  useEffect(() => {
    if (filterData?.tags) {
      // Always update tagFilters when filterData changes to prevent stale cache
      setTagFilters(filterData.tags);
    }
    if (filterData?.allTags) {
      setAllTags(filterData.allTags);
    }
  }, [filterData?.tags, filterData?.allTags]);

  return {
    tagFilters,
    refreshFilters,
    loading,
    error,
    allTags
  };
};

// Add display name for React DevTools
useTagFilters.displayName = 'useTagFilters';

export default useTagFilters;
